import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { settingsService } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import WebAuthnSetup from '../../components/admin/WebAuthnSetup';
import MigrationPanel from '../../components/admin/MigrationPanel';

// Styled Components
const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const Tab = styled.button`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: none;
  border: none;
  border-bottom: 3px solid ${({ theme, active }) => (active ? theme.colors.primary : 'transparent')};
  color: ${({ theme, active }) => (active ? theme.colors.primary : theme.colors.medium)};
  font-weight: ${({ theme, active }) => (active ? theme.typography.fontWeight.bold : theme.typography.fontWeight.medium)};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const FormSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const FormSectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.dark};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  padding-bottom: ${({ theme }) => theme.spacing[2]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FormGroup = styled.div`
  flex: 1;
  min-width: 250px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.dark};
`;

const Description = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.medium};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Input = styled(Field)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme, error }) => (error ? theme.colors.error : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.dark};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.primary}30`};
  }
`;

const Select = styled(Field)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme, error }) => (error ? theme.colors.error : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.dark};
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.primary}30`};
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  
  input {
    width: auto;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const Message = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: white;
  background-color: ${({ theme, type }) => 
    type === 'success' ? theme.colors.success : 
    type === 'error' ? theme.colors.error : 
    theme.colors.info
  };
`;

const ImportExportButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const StoredPolicyNotice = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[5]};
  padding: ${({ theme }) => theme.spacing[4]};
  border: 1px solid #d89a24;
  border-left: 4px solid #d89a24;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: #fff8e7;
  color: ${({ theme }) => theme.colors.dark};
  line-height: 1.5;

  strong {
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing[1]};
  }
`;

// Notification email-routing types. Each maps to a comma-separated list of
// email addresses stored under the notifications setting `notificationRouting`.
const NOTIFICATION_ROUTING_TYPES = [
  { key: 'timeEntry', label: 'New time entry submitted' },
  { key: 'approval', label: 'Time entry approved' },
  { key: 'rejection', label: 'Time entry challenged' },
  { key: 'assignment', label: 'Assignment changed' },
  { key: 'system', label: 'System messages' },
  { key: 'reminder', label: 'Weekly reminder' },
];

// The Settings API stores allowedIps as a JSON array, while a textarea is much
// easier to review and edit. Accept one value per line or comma-separated and
// remove accidental duplicates without changing the order.
const parseAllowedIps = (value) => [...new Set(
  String(value || '')
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean)
)];

const validIpv6Address = (address) => {
  if (!/^[0-9a-f:]+$/i.test(address) || address.includes(':::')) return false;

  const compressed = address.includes('::');
  const halves = address.split('::');
  if (halves.length > 2) return false;

  const left = halves[0] ? halves[0].split(':') : [];
  const right = compressed && halves[1] ? halves[1].split(':') : [];
  const groups = [...left, ...right];
  const validGroups = groups.every((group) => /^[0-9a-f]{1,4}$/i.test(group));

  // Without compression every 16-bit group must be present. With `::`, at
  // least one of the eight groups must actually be omitted.
  return validGroups && (compressed ? groups.length < 8 : groups.length === 8);
};

const validIpOrCidr = (value) => {
  const [address, prefix, extra] = String(value).split('/');
  if (extra !== undefined) return false;
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(address)) {
    if (!address.split('.').every((part) => Number(part) >= 0 && Number(part) <= 255)) return false;
    return prefix === undefined || (/^\d+$/.test(prefix) && Number(prefix) <= 32);
  }
  if (validIpv6Address(address)) {
    return prefix === undefined || (/^\d+$/.test(prefix) && Number(prefix) <= 128);
  }
  return false;
};

const settingsForForm = (category, values = {}) => {
  if (category !== 'security') return values;
  return {
    ...values,
    allowedIps: Array.isArray(values.allowedIps) ? values.allowedIps.join('\n') : (values.allowedIps || ''),
  };
};

const settingsForApi = (category, values) => {
  if (category === 'notifications') {
    // The retired interval is no longer editable. Do not resubmit legacy
    // values, which could otherwise prevent saving the remaining settings.
    const updates = { ...values };
    delete updates.approvalReminderFrequency;
    return updates;
  }
  if (category !== 'security') return values;
  return { ...values, allowedIps: parseAllowedIps(values.allowedIps) };
};

export { parseAllowedIps, validIpOrCidr, settingsForForm, settingsForApi };

const Settings = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [message, setMessage] = useState(null);
  const [testingReport, setTestingReport] = useState(false);
  const [testReportResult, setTestReportResult] = useState(null);

  const fetchSettings = async (category) => {
    // The Migration tab is not a settings category — it manages its own data.
    if (category === 'migration') {
      setSettings({});
      setLoadError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const response = await settingsService.getSettingsByCategory(category);
      setSettings(settingsForForm(category, response.data || {}));
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoadError(true);
      setSettings({});
    } finally {
      setLoading(false);
    }
  };

  // Load settings on initial mount and tab change
  useEffect(() => {
    fetchSettings(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMessage(null);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await settingsService.updateSettings(activeTab, settingsForApi(activeTab, values));
      setSettings(settingsForForm(activeTab, response.data));
      setMessage({
        type: 'success',
        text: 'Settings updated successfully'
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error updating settings:', error);
      setMessage({
        type: 'error',
        text: 'Failed to update settings. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Run the nightly reminder job now so the admin can confirm their email config.
  const handleSendTestReport = async () => {
    setTestingReport(true);
    setTestReportResult(null);
    try {
      const res = await settingsService.runDailyReminders();
      const d = (res && res.data) || res || {};
      const counts = `${d.missingEmployees ?? 0} employee(s) missing, ${d.pendingCustomers ?? 0} customer(s) pending`;
      // v1.42.0: the report goes through the outbox, so `mailed` only means it
      // was handed over — `delivery` says whether it went now or is waiting
      // for the mail limit.
      const delivery = d.delivery || {};
      let mailMsg;
      if (d.mailConfigured === false) mailMsg = 'SMTP is not configured, so no email was sent';
      else if (delivery.sent) mailMsg = 'report e-mail sent';
      else if (delivery.queued) mailMsg = 'report e-mail queued — it will go out as soon as the mail limit allows';
      else if (delivery.failed) mailMsg = 'report e-mail failed — see E-mail delivery for the error';
      else if (d.mailed) mailMsg = 'report email sent';
      else mailMsg = 'no admin recipient configured (add one above or set active admins)';
      setTestReportResult(`Done — ${counts}; ${mailMsg}.`);
    } catch (error) {
      setTestReportResult(`Failed: ${error.message}`);
    } finally {
      setTestingReport(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Reset settings that have declared defaults? Settings without a declared default will be kept.')) {
      try {
        const response = await settingsService.resetSettings(activeTab);
        setSettings(settingsForForm(activeTab, response.data));
        setMessage({
          type: 'success',
          text: response.message || 'Settings with declared defaults reset successfully'
        });
      } catch (error) {
        console.error('Error resetting settings:', error);
        setMessage({
          type: 'error',
          text: 'Failed to reset settings. Please try again.'
        });
      }
    }
  };

  const handleExport = async () => {
    try {
      const response = await settingsService.exportSettings();
      
      // Create downloadable JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
      
      const exportFileDefaultName = `timereporting-settings-${new Date().toISOString().slice(0, 10)}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      setMessage({
        type: 'success',
        text: 'Settings exported successfully'
      });
    } catch (error) {
      console.error('Error exporting settings:', error);
      setMessage({
        type: 'error',
        text: 'Failed to export settings. Please try again.'
      });
    }
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = (event) => {
      const file = event.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const settingsData = JSON.parse(e.target.result);
          await settingsService.importSettings(settingsData);

          // Reload current category settings
          await fetchSettings(activeTab);

          setMessage({
            type: 'success',
            text: 'Settings imported successfully'
          });
        } catch (error) {
          console.error('Error importing settings:', error);
          setMessage({
            type: 'error',
            text: 'Failed to import settings. Please check file format and try again.'
          });
        }
      };
      
      reader.readAsText(file);
    };
    
    input.click();
  };

  // Get validation schema based on active tab
  const getValidationSchema = () => {
    switch (activeTab) {
      case 'general':
        return Yup.object({
          companyName: Yup.string().required('Company name is required'),
          companyEmail: Yup.string().email('Invalid email address').required('Company email is required'),
          companyPhone: Yup.string().required('Company phone is required'),
          companyAddress: Yup.string().required('Company address is required'),
          companyLogo: Yup.string().url('Company logo must be a valid URL'),
          dateFormat: Yup.string().required('Date format is required'),
          timeFormat: Yup.string().required('Time format is required'),
          defaultLanguage: Yup.string().required('Default language is required'),
          timezone: Yup.string().required('Timezone is required'),
          eurDkkRate: Yup.number()
            .transform((value, originalValue) => (originalValue === '' || originalValue == null ? null : value))
            .nullable()
            .typeError('Exchange rate must be a number')
            .positive('Exchange rate must be a positive number'),
        });
      
      case 'timeTracking':
        return Yup.object({
          workWeekStart: Yup.string().required('Work week start is required'),
          minimumHours: Yup.number().min(0).required('Minimum hours is required'),
          maximumHours: Yup.number().min(1).required('Maximum hours is required'),
          timeIncrements: Yup.number().required('Time increment is required'),
          overtimeRate: Yup.number().min(1).required('Overtime rate is required'),
          lockPastTimeEntries: Yup.number().min(0).required('Lock past entries is required'),
        });
      
      case 'approval':
        return Yup.object({
          autoApproveBelowHours: Yup.number().min(0).required('Auto-approve threshold is required'),
          escalateAfterDays: Yup.number().min(0).required('Escalation days is required'),
          requiredApprovers: Yup.number().min(0).required('Required approvers is required'),
        });
      
      case 'notifications':
        return Yup.object({
          digestTime: Yup.string().required('Digest time is required'),
        });
      
      case 'security':
        return Yup.object({
          passwordExpiryDays: Yup.number().min(0).required('Password expiry is required'),
          sessionTimeout: Yup.number().min(1).required('Session timeout is required'),
          maxLoginAttempts: Yup.number().min(1).required('Max login attempts is required'),
          lockoutDuration: Yup.number().min(0).required('Lockout duration is required'),
          allowedIps: Yup.string().test(
            'allowed-ip-list',
            'Enter valid IPv4 or IPv6 addresses, optionally with CIDR ranges',
            (value) => parseAllowedIps(value).every(validIpOrCidr)
          ),
        });
      
      case 'appearance':
        return Yup.object({
          theme: Yup.string().required('Theme is required'),
          primaryColor: Yup.string().required('Primary color is required'),
          accentColor: Yup.string().required('Accent color is required'),
          welcomeMessage: Yup.string().when('showWelcomeMessage', {
            is: true,
            then: () => Yup.string().required('Welcome message is required')
          }),
          dashboardLayout: Yup.string().required('Dashboard layout is required'),
        });
      
      default:
        return Yup.object({});
    }
  };

  // Render form based on active tab
  const renderForm = () => {
    if (loading) {
      return <div>Loading settings...</div>;
    }

    if (loadError) {
      return (
        <div>
          <Message type="error">
            Failed to load settings. Please check your connection and try again.
          </Message>
          <Button type="button" variant="primary" onClick={() => fetchSettings(activeTab)}>
            Retry
          </Button>
        </div>
      );
    }

    // Merge defaults for keys that may not have a stored row yet, so their
    // Formik fields stay controlled (e.g. the nightly-report settings).
    const tabDefaults = activeTab === 'notifications'
      ? { adminReportEmail: '', dailyReminders: true, customerApprovalReminders: true }
      : activeTab === 'general'
        ? { eurDkkRate: '' }
        : {};

    return (
      <Formik
        initialValues={{ ...tabDefaults, ...settings }}
        validationSchema={getValidationSchema()}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, isSubmitting, resetForm }) => (
          <Form>
            {message && (
              <Message type={message.type}>{message.text}</Message>
            )}

            {activeTab === 'general' && (
              <>
                <FormSection>
                  <FormSectionTitle>Company Information</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="companyName">Company Name</Label>
                      <Input
                        type="text"
                        id="companyName"
                        name="companyName"
                        error={errors.companyName && touched.companyName}
                      />
                      {errors.companyName && touched.companyName && (
                        <ErrorMessage>{errors.companyName}</ErrorMessage>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="companyEmail">Company Email</Label>
                      <Input
                        type="email"
                        id="companyEmail"
                        name="companyEmail"
                        error={errors.companyEmail && touched.companyEmail}
                      />
                      {errors.companyEmail && touched.companyEmail && (
                        <ErrorMessage>{errors.companyEmail}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="companyPhone">Company Phone</Label>
                      <Input
                        type="text"
                        id="companyPhone"
                        name="companyPhone"
                        error={errors.companyPhone && touched.companyPhone}
                      />
                      {errors.companyPhone && touched.companyPhone && (
                        <ErrorMessage>{errors.companyPhone}</ErrorMessage>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="companyWebsite">Company Website</Label>
                      <Input
                        type="text"
                        id="companyWebsite"
                        name="companyWebsite"
                        error={errors.companyWebsite && touched.companyWebsite}
                      />
                      {errors.companyWebsite && touched.companyWebsite && (
                        <ErrorMessage>{errors.companyWebsite}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="companyAddress">Company Address</Label>
                      <Input
                        forwardedAs="textarea"
                        rows="3"
                        id="companyAddress"
                        name="companyAddress"
                        error={errors.companyAddress && touched.companyAddress}
                      />
                      {errors.companyAddress && touched.companyAddress && (
                        <ErrorMessage>{errors.companyAddress}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="companyLogo">Company Logo URL</Label>
                      <Input
                        type="url"
                        id="companyLogo"
                        name="companyLogo"
                        error={errors.companyLogo && touched.companyLogo}
                      />
                      <Description>
                        Stored setting only. Tempus currently uses the bundled Vikar Experten logo.
                      </Description>
                      {errors.companyLogo && touched.companyLogo && (
                        <ErrorMessage>{errors.companyLogo}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Regional Settings</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="dateFormat">Date Format</Label>
                      <Select
                        forwardedAs="select"
                        id="dateFormat"
                        name="dateFormat"
                        error={errors.dateFormat && touched.dateFormat}
                      >
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      </Select>
                      {errors.dateFormat && touched.dateFormat && (
                        <ErrorMessage>{errors.dateFormat}</ErrorMessage>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="timeFormat">Time Format</Label>
                      <Select
                        forwardedAs="select"
                        id="timeFormat"
                        name="timeFormat"
                        error={errors.timeFormat && touched.timeFormat}
                      >
                        <option value="12h">12 Hour (AM/PM)</option>
                        <option value="24h">24 Hour</option>
                      </Select>
                      {errors.timeFormat && touched.timeFormat && (
                        <ErrorMessage>{errors.timeFormat}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="defaultLanguage">Default Language</Label>
                      <Select
                        forwardedAs="select"
                        id="defaultLanguage"
                        name="defaultLanguage"
                        error={errors.defaultLanguage && touched.defaultLanguage}
                      >
                        <option value="en-US">English (US)</option>
                        <option value="en-GB">English (GB)</option>
                        <option value="sv-SE">Swedish</option>
                        <option value="de-DE">German</option>
                        <option value="fr-FR">French</option>
                      </Select>
                      {errors.defaultLanguage && touched.defaultLanguage && (
                        <ErrorMessage>{errors.defaultLanguage}</ErrorMessage>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="timezone">Default Timezone</Label>
                      <Select
                        forwardedAs="select"
                        id="timezone"
                        name="timezone"
                        error={errors.timezone && touched.timezone}
                      >
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="Europe/London">London (GMT)</option>
                        <option value="Europe/Paris">Central European Time (CET)</option>
                        <option value="Europe/Copenhagen">Copenhagen</option>
                        <option value="Europe/Stockholm">Stockholm</option>
                      </Select>
                      {errors.timezone && touched.timezone && (
                        <ErrorMessage>{errors.timezone}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>EUR/DKK Exchange Rate</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="eurDkkRate">EUR/DKK exchange rate</Label>
                      <Input
                        type="number"
                        id="eurDkkRate"
                        name="eurDkkRate"
                        step="0.0001"
                        min="0.0001"
                        error={errors.eurDkkRate && touched.eurDkkRate}
                      />
                      <Description>
                        Converts each employee&apos;s EUR hourly wage to the informational
                        Bruttoløn column on the Payroll page. Not transferred to Danløn.
                      </Description>
                      {errors.eurDkkRate && touched.eurDkkRate && (
                        <ErrorMessage>{errors.eurDkkRate}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                </FormSection>
              </>
            )}

            {activeTab === 'timeTracking' && (
              <>
                <FormSection>
                  <FormSectionTitle>Time Entry Settings</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="workWeekStart">Work Week Starts On</Label>
                      <Select
                        forwardedAs="select"
                        id="workWeekStart"
                        name="workWeekStart"
                        error={errors.workWeekStart && touched.workWeekStart}
                      >
                        <option value="monday">Monday</option>
                        <option value="sunday">Sunday</option>
                      </Select>
                      {errors.workWeekStart && touched.workWeekStart && (
                        <ErrorMessage>{errors.workWeekStart}</ErrorMessage>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="timeIncrements">Time Entry Increments (minutes)</Label>
                      <Select
                        forwardedAs="select"
                        id="timeIncrements"
                        name="timeIncrements"
                        error={errors.timeIncrements && touched.timeIncrements}
                      >
                        <option value="5">5 Minutes</option>
                        <option value="10">10 Minutes</option>
                        <option value="15">15 Minutes</option>
                        <option value="30">30 Minutes</option>
                        <option value="60">60 Minutes</option>
                      </Select>
                      {errors.timeIncrements && touched.timeIncrements && (
                        <ErrorMessage>{errors.timeIncrements}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="minimumHours">Minimum Hours per Entry</Label>
                      <Input
                        type="number"
                        id="minimumHours"
                        name="minimumHours"
                        min="0"
                        step="0.1"
                        error={errors.minimumHours && touched.minimumHours}
                      />
                      {errors.minimumHours && touched.minimumHours && (
                        <ErrorMessage>{errors.minimumHours}</ErrorMessage>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="maximumHours">Maximum Hours per Entry</Label>
                      <Input
                        type="number"
                        id="maximumHours"
                        name="maximumHours"
                        min="1"
                        step="0.1"
                        error={errors.maximumHours && touched.maximumHours}
                      />
                      {errors.maximumHours && touched.maximumHours && (
                        <ErrorMessage>{errors.maximumHours}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="overtimeRate">Overtime Rate Multiplier</Label>
                      <Input
                        type="number"
                        id="overtimeRate"
                        name="overtimeRate"
                        min="1"
                        step="0.1"
                        error={errors.overtimeRate && touched.overtimeRate}
                      />
                      {errors.overtimeRate && touched.overtimeRate && (
                        <ErrorMessage>{errors.overtimeRate}</ErrorMessage>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="lockPastTimeEntries">Lock Past Time Entries After (days)</Label>
                      <Input
                        type="number"
                        id="lockPastTimeEntries"
                        name="lockPastTimeEntries"
                        min="0"
                        error={errors.lockPastTimeEntries && touched.lockPastTimeEntries}
                      />
                      <Description>0 = Never lock</Description>
                      {errors.lockPastTimeEntries && touched.lockPastTimeEntries && (
                        <ErrorMessage>{errors.lockPastTimeEntries}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="roundTimeEntries">Round Time Entries</Label>
                      <Select
                        forwardedAs="select"
                        id="roundTimeEntries"
                        name="roundTimeEntries"
                        error={errors.roundTimeEntries && touched.roundTimeEntries}
                      >
                        <option value="nearest">To Nearest Increment</option>
                        <option value="up">Always Round Up</option>
                        <option value="down">Always Round Down</option>
                        <option value="none">No Rounding</option>
                      </Select>
                      {errors.roundTimeEntries && touched.roundTimeEntries && (
                        <ErrorMessage>{errors.roundTimeEntries}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Default Requirements</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="allowFutureTimeEntries"
                          name="allowFutureTimeEntries"
                        />
                        <Label htmlFor="allowFutureTimeEntries">Allow Future Time Entries</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="defaultProjectRequired"
                          name="defaultProjectRequired"
                        />
                        <Label htmlFor="defaultProjectRequired">Require Project for Time Entries</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="defaultApprovalRequired"
                          name="defaultApprovalRequired"
                        />
                        <Label htmlFor="defaultApprovalRequired">Require Approval for Time Entries</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                </FormSection>
              </>
            )}

            {activeTab === 'approval' && (
              <FormSection>
                <FormSectionTitle>Approval Settings</FormSectionTitle>
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="autoApproveBelowHours">Auto-Approve Time Entries Below (hours)</Label>
                    <Input
                      type="number"
                      id="autoApproveBelowHours"
                      name="autoApproveBelowHours"
                      min="0"
                      step="0.1"
                      error={errors.autoApproveBelowHours && touched.autoApproveBelowHours}
                    />
                    <Description>0 = Never auto-approve</Description>
                    {errors.autoApproveBelowHours && touched.autoApproveBelowHours && (
                      <ErrorMessage>{errors.autoApproveBelowHours}</ErrorMessage>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="escalateAfterDays">Escalate Unapproved Entries After (days)</Label>
                    <Input
                      type="number"
                      id="escalateAfterDays"
                      name="escalateAfterDays"
                      min="0"
                      error={errors.escalateAfterDays && touched.escalateAfterDays}
                    />
                    <Description>0 = Never escalate</Description>
                    {errors.escalateAfterDays && touched.escalateAfterDays && (
                      <ErrorMessage>{errors.escalateAfterDays}</ErrorMessage>
                    )}
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="requiredApprovers">Number of Required Approvers</Label>
                    <Input
                      type="number"
                      id="requiredApprovers"
                      name="requiredApprovers"
                      min="0"
                      max="5"
                      error={errors.requiredApprovers && touched.requiredApprovers}
                    />
                    {errors.requiredApprovers && touched.requiredApprovers && (
                      <ErrorMessage>{errors.requiredApprovers}</ErrorMessage>
                    )}
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Checkbox>
                      <Field
                        type="checkbox"
                        id="allowSelfApproval"
                        name="allowSelfApproval"
                      />
                      <Label htmlFor="allowSelfApproval">Allow Self-Approval</Label>
                    </Checkbox>
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Checkbox>
                      <Field
                        type="checkbox"
                        id="enableCustomerApproval"
                        name="enableCustomerApproval"
                      />
                      <Label htmlFor="enableCustomerApproval">Enable Customer Approval</Label>
                    </Checkbox>
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Checkbox>
                      <Field
                        type="checkbox"
                        id="notifyOnApproval"
                        name="notifyOnApproval"
                      />
                      <Label htmlFor="notifyOnApproval">Send Notification on Approval</Label>
                    </Checkbox>
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Checkbox>
                      <Field
                        type="checkbox"
                        id="notifyOnRejection"
                        name="notifyOnRejection"
                      />
                      <Label htmlFor="notifyOnRejection">Send Notification on Rejection</Label>
                    </Checkbox>
                  </FormGroup>
                </FormRow>
              </FormSection>
            )}

            {activeTab === 'notifications' && (
              <>
                <FormSection>
                  <FormSectionTitle>Time Entry Reminders</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="timeEntryReminders"
                          name="timeEntryReminders"
                        />
                        <Label htmlFor="timeEntryReminders">Send daily employee reminders for unsaved assigned days</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  <Description>
                    Employees receive one daily email at 02:00 Copenhagen time when assigned days before today have no saved registration.
                    A saved draft or saved Free day counts. Each email includes their personal login link.
                    The weekly submission report goes only to administrators on Mondays at 08:00.
                  </Description>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Approval Reminders</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="approvalReminders"
                          name="approvalReminders"
                        />
                        <Label htmlFor="approvalReminders">Send Approval Reminders</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  <Description>
                    Customer approval reminders are sent every Monday at 06:00 Danish local time (CET/CEST)
                    for all finished/submitted hours still awaiting approval, including recent registrations.
                    Each customer receives at most one reminder per Monday. Existing reminder opt-outs apply.
                  </Description>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Nightly reminder reports (02:00)</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field type="checkbox" id="dailyReminders" name="dailyReminders" />
                        <Label htmlFor="dailyReminders">Enable reminders and the nightly admin report (also required for Monday customer reminders)</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field type="checkbox" id="customerApprovalReminders" name="customerApprovalReminders" />
                        <Label htmlFor="customerApprovalReminders">Send customer reminders on Mondays at 06:00 Danish time for hours awaiting approval; honors each customer's setting</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="adminReportEmail">Admin report email(s)</Label>
                      <Input
                        type="text"
                        id="adminReportEmail"
                        name="adminReportEmail"
                        placeholder="reports@example.com, payroll@example.com"
                      />
                      <Description>
                        Where the nightly missing-registration / missing-approval reports are sent.
                        Comma-separated. Leave blank to send to all active admins.
                      </Description>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Button type="button" variant="secondary" onClick={handleSendTestReport} disabled={testingReport}>
                        {testingReport ? 'Sending…' : 'Run nightly report now'}
                      </Button>
                      {testReportResult && <Description>{testReportResult}</Description>}
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Notification Delivery</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="emailNotifications"
                          name="emailNotifications"
                        />
                        <Label htmlFor="emailNotifications">Enable Email Notifications</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="inAppNotifications"
                          name="inAppNotifications"
                        />
                        <Label htmlFor="inAppNotifications">Enable In-App Notifications</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="notificationDigest">Notification Digest</Label>
                      <Select
                        forwardedAs="select"
                        id="notificationDigest"
                        name="notificationDigest"
                        error={errors.notificationDigest && touched.notificationDigest}
                      >
                        <option value="immediate">Send Immediately</option>
                        <option value="daily">Daily Digest</option>
                        <option value="weekly">Weekly Digest</option>
                      </Select>
                      {errors.notificationDigest && touched.notificationDigest && (
                        <ErrorMessage>{errors.notificationDigest}</ErrorMessage>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="digestTime">Digest Delivery Time</Label>
                      <Input
                        type="time"
                        id="digestTime"
                        name="digestTime"
                        error={errors.digestTime && touched.digestTime}
                      />
                      {errors.digestTime && touched.digestTime && (
                        <ErrorMessage>{errors.digestTime}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Notification Email Routing</FormSectionTitle>
                  <Description>
                    Route notification emails for each event to specific
                    recipients. Separate multiple addresses with commas. Leave a
                    field blank to send no routed email for that event. Routed
                    emails are sent in addition to in-app notifications.
                  </Description>
                  {NOTIFICATION_ROUTING_TYPES.map(({ key, label }) => (
                    <FormRow key={key}>
                      <FormGroup>
                        <Label htmlFor={`notificationRouting.${key}`}>{label}</Label>
                        <Input
                          type="text"
                          id={`notificationRouting.${key}`}
                          name={`notificationRouting.${key}`}
                          placeholder="name@example.com, name2@example.com"
                        />
                      </FormGroup>
                    </FormRow>
                  ))}
                </FormSection>
              </>
            )}

            {activeTab === 'security' && (
              <>
                <WebAuthnSetup />
                <StoredPolicyNotice role="note">
                  <strong>{t('admin.settingsSecurity.storedPolicyTitle')}</strong>
                  {t('admin.settingsSecurity.storedPolicyBody')}
                </StoredPolicyNotice>
                <FormSection>
                  <FormSectionTitle>{t('admin.settingsSecurity.passwordSection')}</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="passwordExpiryDays">Password Expiry (days)</Label>
                      <Input
                        type="number"
                        id="passwordExpiryDays"
                        name="passwordExpiryDays"
                        min="0"
                        error={errors.passwordExpiryDays && touched.passwordExpiryDays}
                      />
                      <Description>{t('admin.settingsSecurity.passwordExpiryHint')}</Description>
                      {errors.passwordExpiryDays && touched.passwordExpiryDays && (
                        <ErrorMessage>{errors.passwordExpiryDays}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="mfaRequired"
                          name="mfaRequired"
                        />
                        <Label htmlFor="mfaRequired">Require Multi-Factor Authentication</Label>
                      </Checkbox>
                      <Description>{t('admin.settingsSecurity.mfaHint')}</Description>
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>{t('admin.settingsSecurity.sessionSection')}</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="sessionTimeout">Session Timeout (minutes)</Label>
                      <Input
                        type="number"
                        id="sessionTimeout"
                        name="sessionTimeout"
                        min="1"
                        error={errors.sessionTimeout && touched.sessionTimeout}
                      />
                      <Description>{t('admin.settingsSecurity.sessionHint')}</Description>
                      {errors.sessionTimeout && touched.sessionTimeout && (
                        <ErrorMessage>{errors.sessionTimeout}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>{t('admin.settingsSecurity.loginSection')}</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="maxLoginAttempts">Max Login Attempts</Label>
                      <Input
                        type="number"
                        id="maxLoginAttempts"
                        name="maxLoginAttempts"
                        min="1"
                        error={errors.maxLoginAttempts && touched.maxLoginAttempts}
                      />
                      {errors.maxLoginAttempts && touched.maxLoginAttempts && (
                        <ErrorMessage>{errors.maxLoginAttempts}</ErrorMessage>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="lockoutDuration">Lockout Duration (minutes)</Label>
                      <Input
                        type="number"
                        id="lockoutDuration"
                        name="lockoutDuration"
                        min="1"
                        error={errors.lockoutDuration && touched.lockoutDuration}
                      />
                      {errors.lockoutDuration && touched.lockoutDuration && (
                        <ErrorMessage>{errors.lockoutDuration}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <Description>{t('admin.settingsSecurity.loginHint')}</Description>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>{t('admin.settingsSecurity.ipSection')}</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="ipRestriction"
                          name="ipRestriction"
                        />
                        <Label htmlFor="ipRestriction">Restrict Access by IP</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="allowedIps">Allowed IP addresses and ranges</Label>
                      <Input
                        forwardedAs="textarea"
                        rows="5"
                        id="allowedIps"
                        name="allowedIps"
                        placeholder={'192.0.2.10\n2001:db8::/32'}
                        error={errors.allowedIps && touched.allowedIps}
                      />
                      <Description>{t('admin.settingsSecurity.ipHint')}</Description>
                      {errors.allowedIps && touched.allowedIps && (
                        <ErrorMessage>{errors.allowedIps}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                </FormSection>
              </>
            )}

            {activeTab === 'appearance' && (
              <>
                <FormSection>
                  <FormSectionTitle>Theme Settings</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="theme">Theme</Label>
                      <Select
                        forwardedAs="select"
                        id="theme"
                        name="theme"
                        error={errors.theme && touched.theme}
                      >
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                        <option value="auto">Auto (System Default)</option>
                      </Select>
                      {errors.theme && touched.theme && (
                        <ErrorMessage>{errors.theme}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="primaryColor">Primary Color</Label>
                      <Input
                        type="color"
                        id="primaryColor"
                        name="primaryColor"
                        error={errors.primaryColor && touched.primaryColor}
                      />
                      {errors.primaryColor && touched.primaryColor && (
                        <ErrorMessage>{errors.primaryColor}</ErrorMessage>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="accentColor">Accent Color</Label>
                      <Input
                        type="color"
                        id="accentColor"
                        name="accentColor"
                        error={errors.accentColor && touched.accentColor}
                      />
                      {errors.accentColor && touched.accentColor && (
                        <ErrorMessage>{errors.accentColor}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>User Interface</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="showWelcomeMessage"
                          name="showWelcomeMessage"
                        />
                        <Label htmlFor="showWelcomeMessage">Show Welcome Message</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  {settings.showWelcomeMessage && (
                    <FormRow>
                      <FormGroup>
                        <Label htmlFor="welcomeMessage">Welcome Message</Label>
                        <Input
                          type="text"
                          id="welcomeMessage"
                          name="welcomeMessage"
                          error={errors.welcomeMessage && touched.welcomeMessage}
                        />
                        {errors.welcomeMessage && touched.welcomeMessage && (
                          <ErrorMessage>{errors.welcomeMessage}</ErrorMessage>
                        )}
                      </FormGroup>
                    </FormRow>
                  )}
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="dashboardLayout">Dashboard Layout</Label>
                      <Select
                        forwardedAs="select"
                        id="dashboardLayout"
                        name="dashboardLayout"
                        error={errors.dashboardLayout && touched.dashboardLayout}
                      >
                        <option value="default">Default</option>
                        <option value="compact">Compact</option>
                        <option value="detailed">Detailed</option>
                      </Select>
                      {errors.dashboardLayout && touched.dashboardLayout && (
                        <ErrorMessage>{errors.dashboardLayout}</ErrorMessage>
                      )}
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="compactSidebar"
                          name="compactSidebar"
                        />
                        <Label htmlFor="compactSidebar">Compact Sidebar</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup>
                      <Checkbox>
                        <Field
                          type="checkbox"
                          id="enableAnimations"
                          name="enableAnimations"
                        />
                        <Label htmlFor="enableAnimations">Enable Animations</Label>
                      </Checkbox>
                    </FormGroup>
                  </FormRow>
                </FormSection>
              </>
            )}

            <ActionButtons>
              <Button type="button" variant="ghost" onClick={handleReset}>
                Reset to Default
              </Button>
              <Button type="button" variant="secondary" onClick={() => resetForm()}>
                Cancel Changes
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Settings'}
              </Button>
            </ActionButtons>
          </Form>
        )}
      </Formik>
    );
  };

  return (
    <SettingsContainer>
      <Card>
        <TabContainer>
          <Tab
            active={activeTab === 'general'}
            onClick={() => handleTabChange('general')}
          >
            General
          </Tab>
          <Tab
            active={activeTab === 'timeTracking'}
            onClick={() => handleTabChange('timeTracking')}
          >
            Time Tracking
          </Tab>
          <Tab
            active={activeTab === 'approval'}
            onClick={() => handleTabChange('approval')}
          >
            Approval
          </Tab>
          <Tab
            active={activeTab === 'notifications'}
            onClick={() => handleTabChange('notifications')}
          >
            Notifications
          </Tab>
          <Tab
            active={activeTab === 'security'}
            onClick={() => handleTabChange('security')}
          >
            Security
          </Tab>
          <Tab
            active={activeTab === 'appearance'}
            onClick={() => handleTabChange('appearance')}
          >
            Appearance
          </Tab>
          <Tab
            active={activeTab === 'migration'}
            onClick={() => handleTabChange('migration')}
          >
            Migration
          </Tab>
        </TabContainer>

        {activeTab === 'migration' ? <MigrationPanel /> : renderForm()}

        {activeTab !== 'migration' && (
        <ImportExportButtons>
          <Button type="button" variant="outline" onClick={handleExport}>
            Export Settings
          </Button>
          <Button type="button" variant="outline" onClick={handleImport}>
            Import Settings
          </Button>
        </ImportExportButtons>
        )}
      </Card>
    </SettingsContainer>
  );
};

export default Settings;
